import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@shared/services/api';
import { queryKeys } from '@shared/lib/queryKeys';
import { OrgMemberRole } from '@shared/types';
import { toast } from '@shared/lib/toast';

// Organizations are collaboration features and therefore online-only — these
// are plain network queries, deliberately outside the offline-first store.

/** The caller's org memberships (each row carries the nested `org`). */
export const useOrgsQuery = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.orgs.list(),
    queryFn: () => api.getOrgs(),
    staleTime: 60 * 1000,
    retry: 1,
    enabled,
  });

export const useCreateOrgMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, avatarUrl }: { name: string; avatarUrl?: string }) =>
      api.createOrg(name, avatarUrl),
    onSuccess: (org) => {
      qc.invalidateQueries({ queryKey: queryKeys.orgs.all });
      toast.success(`Organization "${org.name}" created`);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create organization');
    },
  });
};

export const useOrgMembersQuery = (orgId: string | null) =>
  useQuery({
    queryKey: queryKeys.orgs.members(orgId ?? ''),
    queryFn: () => api.getOrgMembers(orgId as string),
    enabled: !!orgId,
    staleTime: 60 * 1000,
    retry: 1,
  });

export const useInviteMemberMutation = (orgId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: OrgMemberRole }) =>
      api.inviteMember(orgId, email, role),
    onSuccess: (member) => {
      qc.invalidateQueries({ queryKey: queryKeys.orgs.members(orgId) });
      toast.success(`${member.user?.name ?? member.user?.email ?? 'Member'} added to the organization`);
    },
    onError: (error: unknown) => {
      const resp = (error as { response?: { status?: number; data?: { error?: string } } })?.response;
      if (resp?.status === 404) {
        toast.error("No Hintword account with that email — ask them to sign up first at hintword.com, then invite them.");
      } else if (resp?.status === 409) {
        toast.error('That person is already a member of this organization.');
      } else if (typeof resp?.data?.error === 'string') {
        toast.error(resp.data.error);
      } else {
        toast.error(error instanceof Error ? error.message : 'Failed to invite member');
      }
    },
  });
};

export const useUpdateMemberRoleMutation = (orgId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: OrgMemberRole }) =>
      api.updateMemberRole(orgId, memberId, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.orgs.members(orgId) });
      toast.success('Member role updated');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update role');
    },
  });
};

export const useRemoveMemberMutation = (orgId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => api.removeMember(orgId, memberId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.orgs.members(orgId) });
      toast.success('Member removed');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to remove member');
    },
  });
};
